import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MarkerType } from '@xyflow/react';
import { useCommunity } from '@contexts/CommunityContext';
import {
    apiCreateForm,
    apiCreateStep,
    apiCreateQuestion,
    apiCreateFormAction,
    apiGetForm,
    apiUpdateForm,
    apiUpdateStep,
    apiDeleteStep,
    apiReorderSteps,
    apiUpdateQuestion,
    apiDeleteQuestion,
    apiReorderQuestions,
    apiUpdateFormAction,
    apiDeleteFormAction,
} from '../api/forms';

const STEP_Y_STEP = 170;

let _counter = 0;
const makeStepId = () => `step-${++_counter}-${Math.random().toString(36).slice(2, 6)}`;

let _qCounter = 0;
const makeQId = () => `q-${++_qCounter}-${Math.random().toString(36).slice(2, 6)}`;

let _aCounter = 0;
const makeActionId = () => `action-${++_aCounter}-${Math.random().toString(36).slice(2, 6)}`;

function createInitialStep(t) {
    return {
        id: makeStepId(),
        _serverId: null,
        title: t('builder.defaults.stepTitle', { number: 1 }),
        description: null,
        header: null,
        submitAction: 'next_step',
        submitButtonText: t('builder.defaults.submitButtonText'),
        allowReturn: false,
        submitLink: null,
        questions: [],
    };
}

function mapServerFormToState(form, t) {
    const steps = (form.steps ?? []).map(step => ({
        id: makeStepId(),
        _serverId: step.id,
        title: step.title ?? '',
        description: step.description ?? null,
        header: step.header ?? null,
        submitAction: step.submit?.action ?? 'next_step',
        submitButtonText: step.submit?.button_text ?? t('builder.defaults.submitButtonText'),
        allowReturn: step.allow_return ?? false,
        submitLink: step.submit?.link ?? null,
        questions: (step.questions ?? []).map(q => ({
            id: makeQId(),
            _serverId: q.id,
            type: q.type,
            label: q.label ?? '',
            description: q.description ?? null,
            required: q.required ?? true,
            settings: q.settings ?? null,
            order: q.order ?? 0,
        })),
    }));

    const actions = (form.actions ?? []).map(a => ({
        id: makeActionId(),
        _serverId: a.id,
        type: a.type,
        settings: a.settings ?? {},
        order: a.order ?? 0,
    }));

    return { steps, actions, form };
}

function isValidUrl(str) {
    if (!str?.trim()) return false;
    try { new URL(str); return true; } catch { return false; }
}

function runValidation({ formName, redirectChoice, returnUrl, steps, t }) {
    const errors = { form: {}, steps: {} };
    let isValid = true;

    if (!formName?.trim()) {
        errors.form.formName = t('builder.validation.formName');
        isValid = false;
    }

    if (redirectChoice === 'link') {
        if (!returnUrl?.trim()) {
            errors.form.returnUrl = t('builder.validation.returnUrlRequired');
            isValid = false;
        } else if (!isValidUrl(returnUrl)) {
            errors.form.returnUrl = t('builder.validation.invalidUrl');
            isValid = false;
        }
    }

    if (steps.length === 0) {
        errors.form.steps = t('builder.validation.noSteps');
        isValid = false;
    }

    for (const step of steps) {
        const stepErrors = { questions: {} };
        let stepHasError = false;

        if ((step.questions ?? []).length === 0) {
            stepErrors.noQuestions = t('builder.validation.noQuestions');
            stepHasError = true;
            isValid = false;
        }

        if (!step.title?.trim()) {
            stepErrors.title = t('builder.validation.stepTitle');
            stepHasError = true;
            isValid = false;
        }

        if (step.submitAction === 'link') {
            if (!step.submitLink?.trim()) {
                stepErrors.submitLink = t('builder.validation.submitLinkRequired');
                stepHasError = true;
                isValid = false;
            } else if (!isValidUrl(step.submitLink)) {
                stepErrors.submitLink = t('builder.validation.invalidUrl');
                stepHasError = true;
                isValid = false;
            }
        }

        for (const question of (step.questions ?? [])) {
            const qErrors = {};

            if (!question.label?.trim()) {
                qErrors.label = t('builder.validation.questionLabel');
                stepHasError = true;
                isValid = false;
            }

            if (question.type === 'choices' || question.type === 'select') {
                const opts = question.settings?.options ?? [];
                if (!opts.some(o => o.label?.trim())) {
                    qErrors.options = t('builder.validation.options');
                    stepHasError = true;
                    isValid = false;
                }
            }

            if (Object.keys(qErrors).length > 0) {
                stepErrors.questions[question.id] = qErrors;
            }
        }

        if (stepHasError) {
            errors.steps[step.id] = stepErrors;
        }
    }

    return { isValid, errors };
}

export default function useFormBuilder({ communityId, onSaveSuccess, editFormId = null, templateData = null }) {
    const { t } = useTranslation('formsModals');
    const { community: communityContext } = useCommunity();

    const originalRef = useRef({ stepIds: [], questionIds: {}, actionIds: [] });

    // In edit mode start loading; in create mode (or template mode) start ready
    const [loading, setLoading] = useState(!!editFormId);

    const initialSteps = editFormId ? [] : (templateData
        ? (templateData.steps ?? []).map(step => ({
            id: makeStepId(),
            _serverId: null,
            title: step.title ?? '',
            description: step.description ?? null,
            header: step.header ?? null,
            submitAction: step.submitAction ?? 'next_step',
            submitButtonText: step.submitButtonText ?? t('builder.defaults.submitButtonText'),
            allowReturn: step.allowReturn ?? false,
            submitLink: step.submitLink ?? null,
            questions: (step.questions ?? []).map(q => ({
                id: makeQId(),
                _serverId: null,
                type: q.type,
                label: q.label ?? '',
                description: q.description ?? null,
                required: q.required ?? true,
                settings: q.settings ?? null,
                order: q.order ?? 0,
            })),
        }))
        : [createInitialStep(t)]);

    const [formName, setFormName] = useState(templateData?.title ?? t('builder.defaults.newFormName'));
    const [formActive, setFormActive] = useState(false);
    const [redirectChoice, setRedirectChoice] = useState('community');
    const [returnUrl, setReturnUrl] = useState(null);
    const [allowMultipleAnswers, setAllowMultipleAnswers] = useState(templateData?.allow_multiple_answers ?? false);
    const [banner, setBanner] = useState('');
    const [theme, setTheme] = useState(templateData?.theme ?? 'light');
    const [actions, setActions] = useState(() => (templateData?.actions ?? []).map(a => ({
        id: makeActionId(),
        _serverId: null,
        type: a.type,
        settings: a.settings ?? {},
        order: a.order ?? 0,
    })));
    const [steps, setSteps] = useState(initialSteps);
    const [selectedStepId, setSelectedStepId] = useState(initialSteps[0]?.id ?? null);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({ form: {}, steps: {} });
    const [scrollSignal, setScrollSignal] = useState(0);

    // ── Fetch form for edit mode ──────────────────────────────────────────
    useEffect(() => {
        if (!editFormId) return;

        apiGetForm(editFormId)
            .then(form => {
                const { steps: mappedSteps, actions: mappedActions } = mapServerFormToState(form, t);

                setFormName(form.title ?? t('common:entities.form'));
                setFormActive(form.active ?? false);
                setRedirectChoice(form.return_url ? 'link' : 'community');
                setReturnUrl(form.return_url ?? null);
                setAllowMultipleAnswers(form.allow_multiple_answers ?? false);
                setBanner(form.banner ?? '');
                setTheme(form.theme ?? 'light');
                setActions(mappedActions);
                setSteps(mappedSteps);
                setSelectedStepId(mappedSteps[0]?.id ?? null);

                originalRef.current = {
                    stepIds: (form.steps ?? []).map(s => s.id),
                    questionIds: Object.fromEntries(
                        (form.steps ?? []).map(s => [s.id, (s.questions ?? []).map(q => q.id)])
                    ),
                    actionIds: (form.actions ?? []).map(a => a.id),
                };
            })
            .catch(e => console.error('Failed to load form for editing:', e))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── ReactFlow nodes ───────────────────────────────────────────────────
    const nodes = useMemo(() => {
        const stepNodes = steps.map((step, i) => ({
            id: step.id,
            type: 'stepNode',
            position: { x: 0, y: i * STEP_Y_STEP },
            data: step,
            draggable: false,
            selectable: false,
        }));

        return [
            ...stepNodes,
            {
                id: '__add-block__',
                type: 'addBlockNode',
                position: { x: 0, y: steps.length * STEP_Y_STEP },
                data: {},
                draggable: false,
                selectable: false,
            },
        ];
    }, [steps]);

    // ── ReactFlow edges ───────────────────────────────────────────────────
    const edges = useMemo(() => {
        const result = steps.slice(0, -1).map((step, i) => ({
            id: `e-${step.id}-${steps[i + 1].id}`,
            source: step.id,
            target: steps[i + 1].id,
            type: 'smoothstep',
            style: { stroke: 'var(--surface-7)', strokeWidth: 1.5 },
            markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--surface-7)' },
        }));

        if (steps.length > 0) {
            result.push({
                id: `e-last-add-block`,
                source: steps[steps.length - 1].id,
                target: '__add-block__',
                type: 'smoothstep',
                style: { stroke: 'var(--surface-8)', strokeWidth: 1.5, strokeDasharray: '6 4' },
                markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--surface-8)' },
            });
        }

        return result;
    }, [steps]);

    // ── Step actions ──────────────────────────────────────────────────────
    const addStep = useCallback(() => {
        const n = steps.length + 1;
        const newStep = {
            id: makeStepId(),
            _serverId: null,
            title: t('builder.defaults.stepTitle', { number: n }),
            description: null,
            header: null,
            submitAction: n === 1 ? 'submit' : 'next_step',
            submitButtonText: t('builder.defaults.submitButtonText'),
            allowReturn: false,
            submitLink: null,
            questions: [],
        };
        setSteps(prev => [...prev, newStep]);
        setSelectedStepId(newStep.id);
    }, [steps.length, t]);

    const selectStep = useCallback((id) => setSelectedStepId(id), []);

    const updateStep = useCallback((stepId, fields) => {
        setSteps(prev => prev.map(s => s.id === stepId ? { ...s, ...fields } : s));
        setErrors(prev => {
            if (!prev.steps[stepId]) return prev;
            const changedKeys = Object.keys(fields);
            const newStepErr = { ...prev.steps[stepId] };
            changedKeys.forEach(k => { delete newStepErr[k]; });
            return { ...prev, steps: { ...prev.steps, [stepId]: newStepErr } };
        });
    }, []);

    const deleteStep = useCallback((nodeId) => {
        const index = steps.findIndex(s => s.id === nodeId);
        if (index === -1) return;

        const hasFollowing = index < steps.length - 1;
        const hasPrevious = index > 0;

        const doDeleteFrom = () => {
            setSteps(prev => prev.slice(0, index));
            setSelectedStepId(null);
        };

        const doDeleteOnly = () => {
            setSteps(prev => prev.filter((_, i) => i !== index));
            setSelectedStepId(null);
        };

        if (!hasFollowing) { doDeleteOnly(); return; }

        window.openConfirm({
            title: t('builder.deleteStep.title'),
            subtitle: t('builder.deleteStep.subtitle', { title: steps[index].title }),
            buttons: [
                { text: t('builder.deleteStep.deleteFollowing'), variant: 'contained', color: 'error', onClick: doDeleteFrom },
                { text: hasPrevious ? t('builder.deleteStep.connectNext') : t('builder.deleteStep.keepFollowing'), variant: 'text', onClick: doDeleteOnly },
            ],
        });
    }, [steps, t]);

    // ── Question actions ──────────────────────────────────────────────────
    const addQuestion = useCallback((stepId, { type, label, description = null, required = true, settings = null }) => {
        const newQ = { id: makeQId(), _serverId: null, type, label, description, required, settings, order: 0 };
        setSteps(prev => prev.map(s => {
            if (s.id !== stepId) return s;
            const questions = [...(s.questions ?? []), { ...newQ, order: (s.questions?.length ?? 0) + 1 }];
            return { ...s, questions };
        }));
        setErrors(prev => {
            if (!prev.steps[stepId]?.noQuestions) return prev;
            const { noQuestions: _, ...rest } = prev.steps[stepId];
            return { ...prev, steps: { ...prev.steps, [stepId]: rest } };
        });
        return newQ.id;
    }, []);

    const updateQuestion = useCallback((stepId, questionId, fields) => {
        setSteps(prev => prev.map(s => {
            if (s.id !== stepId) return s;
            return {
                ...s,
                questions: s.questions.map(q =>
                    q.id === questionId ? { ...q, ...fields } : q
                ),
            };
        }));
        setErrors(prev => {
            const stepErr = prev.steps[stepId];
            if (!stepErr?.questions?.[questionId]) return prev;
            const changedKeys = Object.keys(fields);
            const newQErr = { ...stepErr.questions[questionId] };
            if (changedKeys.includes('label')) delete newQErr.label;
            if (changedKeys.includes('settings')) delete newQErr.options;
            return {
                ...prev,
                steps: {
                    ...prev.steps,
                    [stepId]: { ...stepErr, questions: { ...stepErr.questions, [questionId]: newQErr } },
                },
            };
        });
    }, []);

    const removeQuestion = useCallback((stepId, questionId) => {
        setSteps(prev => prev.map(s => {
            if (s.id !== stepId) return s;
            return { ...s, questions: s.questions.filter(q => q.id !== questionId) };
        }));
    }, []);

    const reorderQuestions = useCallback((stepId, orderedQuestions) => {
        setSteps(prev => prev.map(s =>
            s.id === stepId ? { ...s, questions: orderedQuestions } : s
        ));
    }, []);

    // ── Form action CRUD ──────────────────────────────────────────────────
    const addAction = useCallback((type) => {
        const newAction = {
            id: makeActionId(),
            _serverId: null,
            type,
            settings: type === 'link_redirect' ? { url: '' } : {},
            order: 0,
        };
        setActions(prev => [...prev, { ...newAction, order: prev.length + 1 }]);
    }, []);

    const updateAction = useCallback((actionId, fields) => {
        setActions(prev => prev.map(a => a.id === actionId ? { ...a, ...fields } : a));
    }, []);

    const removeAction = useCallback((actionId) => {
        setActions(prev => prev.filter(a => a.id !== actionId));
    }, []);

    // ── Clear a specific error ────────────────────────────────────────────
    const clearError = useCallback((path) => {
        setErrors(prev => {
            if (path[0] === 'form') {
                if (!prev.form[path[1]]) return prev;
                const { [path[1]]: _, ...rest } = prev.form;
                return { ...prev, form: rest };
            }
            if (path[0] === 'steps') {
                const [, stepId, field] = path;
                if (!prev.steps[stepId]) return prev;
                if (field === 'questions') {
                    const [,, , qId, qField] = path;
                    const stepErr = prev.steps[stepId];
                    if (!stepErr?.questions?.[qId]?.[qField]) return prev;
                    const newQErr = { ...stepErr.questions[qId] };
                    delete newQErr[qField];
                    return {
                        ...prev,
                        steps: {
                            ...prev.steps,
                            [stepId]: { ...stepErr, questions: { ...stepErr.questions, [qId]: newQErr } },
                        },
                    };
                }
                if (!prev.steps[stepId][field]) return prev;
                const { [field]: _, ...rest } = prev.steps[stepId];
                return { ...prev, steps: { ...prev.steps, [stepId]: rest } };
            }
            return prev;
        });
    }, []);

    // ── Navigate to first validation error ────────────────────────────────
    function applyValidationErrors(newErrors) {
        setErrors(newErrors);
        setScrollSignal(s => s + 1);

        const noQuestionsError = Object.values(newErrors.steps).find(s => s.noQuestions);
        if (noQuestionsError) {
            window.setSnackbar?.(noQuestionsError.noQuestions);
        }

        if (Object.keys(newErrors.form).length > 0) {
            setSelectedStepId(null);
        } else {
            const firstErrorStepId = Object.keys(newErrors.steps)[0];
            if (firstErrorStepId) setSelectedStepId(firstErrorStepId);
        }
    }

    // ── Persist entire form to API (create mode) ──────────────────────────
    async function persistForm(active) {
        const communitySlug = communityContext.get(communityId)?.slug;
        const communityUrl = communitySlug
            ? `${window.location.origin}/${communitySlug}`
            : null;

        const formData = await apiCreateForm(communityId, {
            title: formName.trim(),
            active,
            return_url: redirectChoice === 'link' ? (returnUrl || null) : communityUrl,
            allow_multiple_answers: allowMultipleAnswers,
            banner: banner || null,
            theme,
        });
        const serverFormId = formData.id;

        for (const step of steps) {
            const stepData = await apiCreateStep(serverFormId, {
                title: step.title,
                description: step.description ?? null,
                header: step.header ?? null,
                submit_action: step.submitAction,
                submit_button_text: step.submitButtonText,
                allow_return: step.allowReturn,
                submit_link: step.submitLink ?? null,
            });
            const serverStepId = stepData.id;

            for (const question of (step.questions ?? [])) {
                await apiCreateQuestion(serverFormId, serverStepId, {
                    type: question.type,
                    label: question.label,
                    description: question.description ?? null,
                    required: question.required ?? true,
                    settings: question.settings ?? null,
                });
            }
        }

        for (const action of actions) {
            await apiCreateFormAction(serverFormId, {
                type: action.type,
                settings: action.settings ?? {},
                order: action.order,
            });
        }

        return serverFormId;
    }

    // ── Persist form changes to API (edit mode) ───────────────────────────
    async function persistEditedForm(active) {
        const communitySlug = communityContext.get(communityId)?.slug;
        const communityUrl = communitySlug
            ? `${window.location.origin}/${communitySlug}`
            : null;

        await apiUpdateForm(editFormId, {
            title: formName.trim(),
            active,
            return_url: redirectChoice === 'link' ? (returnUrl || null) : communityUrl,
            allow_multiple_answers: allowMultipleAnswers,
            banner: banner || null,
            theme,
        });

        const orig = originalRef.current;

        // Delete steps that were removed
        const currentStepServerIds = new Set(steps.map(s => s._serverId).filter(Boolean));
        for (const origStepId of orig.stepIds) {
            if (!currentStepServerIds.has(origStepId)) {
                await apiDeleteStep(editFormId, origStepId);
            }
        }

        // Upsert steps in order, then reconcile their questions
        const finalStepServerIds = [];
        for (const step of steps) {
            let serverStepId = step._serverId;

            const stepPayload = {
                title: step.title,
                description: step.description ?? null,
                header: step.header ?? null,
                submit_action: step.submitAction,
                submit_button_text: step.submitButtonText,
                allow_return: step.allowReturn,
                submit_link: step.submitLink ?? null,
            };

            if (serverStepId) {
                await apiUpdateStep(editFormId, serverStepId, stepPayload);
            } else {
                const stepData = await apiCreateStep(editFormId, stepPayload);
                serverStepId = stepData.id;
            }

            finalStepServerIds.push(serverStepId);

            // Reconcile questions
            const origQIds = orig.questionIds[step._serverId] ?? [];
            const currentQServerIds = new Set(step.questions.map(q => q._serverId).filter(Boolean));

            for (const origQId of origQIds) {
                if (!currentQServerIds.has(origQId)) {
                    await apiDeleteQuestion(editFormId, serverStepId, origQId);
                }
            }

            const finalQServerIds = [];
            for (const question of (step.questions ?? [])) {
                let serverQId = question._serverId;

                const qPayload = {
                    type: question.type,
                    label: question.label,
                    description: question.description ?? null,
                    required: question.required ?? true,
                    settings: question.settings ?? null,
                };

                if (serverQId) {
                    await apiUpdateQuestion(editFormId, serverStepId, serverQId, qPayload);
                } else {
                    const qData = await apiCreateQuestion(editFormId, serverStepId, qPayload);
                    serverQId = qData.id;
                }

                finalQServerIds.push(serverQId);
            }

            if (finalQServerIds.length > 0) {
                await apiReorderQuestions(editFormId, serverStepId, finalQServerIds);
            }
        }

        if (finalStepServerIds.length > 0) {
            await apiReorderSteps(editFormId, finalStepServerIds);
        }

        // Reconcile actions
        const currentActionServerIds = new Set(actions.map(a => a._serverId).filter(Boolean));
        for (const origActionId of orig.actionIds) {
            if (!currentActionServerIds.has(origActionId)) {
                await apiDeleteFormAction(editFormId, origActionId);
            }
        }

        for (const action of actions) {
            if (action._serverId) {
                await apiUpdateFormAction(editFormId, action._serverId, {
                    type: action.type,
                    settings: action.settings ?? {},
                    order: action.order,
                });
            } else {
                await apiCreateFormAction(editFormId, {
                    type: action.type,
                    settings: action.settings ?? {},
                    order: action.order,
                });
            }
        }
    }

    // ── Save ──────────────────────────────────────────────────────────────
    async function handleSave() {
        const result = runValidation({ formName, redirectChoice, returnUrl, steps, t });
        if (!result.isValid) {
            applyValidationErrors(result.errors);
            return;
        }

        setSaving(true);
        try {
            if (editFormId) {
                await persistEditedForm(formActive);
            } else {
                await persistForm(formActive);
            }
            onSaveSuccess?.();
        } catch (e) {
            console.error('Save failed:', e);
        } finally {
            setSaving(false);
        }
    }

    // ── Activate ──────────────────────────────────────────────────────────
    async function handleActivate() {
        const result = runValidation({ formName, redirectChoice, returnUrl, steps, t });
        if (!result.isValid) {
            applyValidationErrors(result.errors);
            return;
        }

        setSaving(true);
        try {
            if (editFormId) {
                await persistEditedForm(true);
            } else {
                await persistForm(true);
            }
            onSaveSuccess?.();
        } catch (e) {
            console.error('Activate failed:', e);
        } finally {
            setSaving(false);
        }
    }

    const selectedStep = steps.find(s => s.id === selectedStepId) ?? null;

    return {
        formName, setFormName,
        formActive, setFormActive,
        redirectChoice, setRedirectChoice,
        returnUrl, setReturnUrl,
        allowMultipleAnswers, setAllowMultipleAnswers,
        banner, setBanner,
        theme, setTheme,
        actions, addAction, updateAction, removeAction,
        nodes, edges, steps,
        selectedStepId, selectedStep,
        selectStep, addStep, updateStep, deleteStep,
        addQuestion, updateQuestion, removeQuestion, reorderQuestions,
        saving, handleSave, handleActivate,
        errors, clearError,
        scrollSignal,
        loading,
        editFormId,
    };
}
